/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ExtensionAPI, type ExtensionManifest } from '@atlaskit/editor-common/extensions';
import { shouldBlockExtensionDueToAppAccessPolicy } from '../render';
import { parse } from '@atlassian/cs-ari';
import { getExtensionType, isCustomUI as getIsCustomUI } from '@atlassian/forge-ui';
import { getDocument } from '@atlaskit/browser-apis';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';

import { type ForgeUIExtensionType } from '@atlassian/forge-ui-types';
import { jsx } from '@compiled/react';
import { EXTENSION_NAMESPACE } from '../utils/constants';
import { createTitleWithEnvironmentInfo } from './getForgeExtensionProvider';
import { createLocalId } from '../utils/createLocalId';
import { extractKeyFromId } from '../utils/extractKeyFromId';
import Icon from './icon';
import {
	type ForgeConfigPayload,
	type renderConfigHelper,
	validateForgeConfigPayload,
	handleNodeUpdateError,
	transformConfigAfter,
	transformConfigBefore,
} from '../config';
import {
	type ForgeExtensionProviderSharedParams,
	type ForgeCreateExtensionProps,
	type ForgeExtensionManifest,
	type ForgeExtensionParameters,
	type UpdateFunction,
} from '../types';
import { createConnectToForgeGetFieldsDefinitionFunction } from '../config/getFieldsDefinition';
import { AnalyticsAction, type AnalyticsClient } from '../utils/analytics';
import type {
	AutoConvertDefinition,
	AutoConvertMatcherToExtensionsMap,
} from './macro-auto-convert/types';
import { makeAutoConvertDropdownOptions } from './macro-auto-convert/utils/makeAutoConvertDropdownOptions';
import { renderBodiedExtensionIframe } from '../config/EditorForgeConfigHelperRenderer';
import type { ForgeEmbeddedMacroContext } from '../types/extension';
import { type IntlShape } from 'react-intl';
import { messages } from '../render/i18n';

export const manifestIcons = {
	'16': (): Promise<typeof import('./icon')> =>
		import(/* webpackChunkName: "@atlaskit-internal_icon-16" */ './icon'),
	'24': (): Promise<typeof import('./icon')> =>
		import(/* webpackChunkName: "@atlaskit-internal_icon-24" */ './icon'),
	'48': (): Promise<typeof import('./icon')> =>
		import(/* webpackChunkName: "@atlaskit-internal_icon-48" */ './icon'),
};

type ExtensionMapperParams = Pick<
	ForgeExtensionProviderSharedParams,
	'editorActions' | 'createRenderFunction'
> & {
	analyticsClient: AnalyticsClient;
	autoConvertDefinitions?: AutoConvertDefinition[];
	autoConvertMatcherToExtensionsMap?: AutoConvertMatcherToExtensionsMap;
	connectToForgeBlockedExtensions: Set<unknown>;
	embeddedMacroContext: ForgeEmbeddedMacroContext;
	fieldsModules: ExtensionManifest<ForgeExtensionParameters>['modules']['fields'];
	intl: IntlShape;
	renderConfigHelper: typeof renderConfigHelper;
	updateFunction: UpdateFunction;
};

export const updateNodeWithPayload = (
	actions: ExtensionAPI,
	localId: string,
	payload: ForgeConfigPayload,
): void => {
	try {
		actions.doc.update(localId, (currentValue) =>
			mergeNodeWithPayload(currentValue?.attrs || {}, payload),
		);
	} catch (error: unknown) {
		handleNodeUpdateError(error);
	}
};

export const mergeNodeWithPayload = <T extends Record<string, unknown>>(
	attrs: T,
	{ config, body }: ForgeConfigPayload,
): { attrs: T } & Pick<ForgeCreateExtensionProps, 'content'> => {
	// HOTFIX for HOT-115700
	// https://ecosystem-platform.atlassian.net/browse/EXT-2877
	// Remove "forgeEnvironment" and "render" from the node attributes, as they would break validation
	// and therefore prevent the user from submitting a macro configuration.
	const { forgeEnvironment: _forgeEnvironment, render: _render, ...cleanedAttrs } = attrs;

	return {
		attrs: {
			...(cleanedAttrs as T),
			parameters: {
				...((cleanedAttrs as T).parameters || {}),
				...transformConfigAfter(config),
			},
		},
		...(body ? { content: body.content } : {}),
	};
};

export const updateAndMeasureNodeWithPayload = (
	actions: ExtensionAPI,
	localId: string,
	payload: ForgeConfigPayload,
): void => {
	try {
		const { node } = actions!.getNodeWithPosByLocalId(localId);
		const nodeEl = getDocument()?.querySelector(`[extensionkey="${node?.attrs.extensionKey}"]`);

		if (nodeEl) {
			setTimeout(() => {
				const rectHeight = nodeEl?.getBoundingClientRect().height;

				actions.doc.update(localId, (currentValue) => {
					const result = mergeNodeWithPayload(currentValue.attrs || {}, payload);
					const newNode = {
						attrs: {
							...result.attrs,
							parameters: {
								...result.attrs.parameters,
								measuredHeight: rectHeight,
							},
						},
						...(result.content ? { content: result.content } : {}),
					};

					return newNode;
				});
			}, 3000);
		} else {
			actions.doc.update(localId, (currentValue) =>
				mergeNodeWithPayload(currentValue?.attrs || {}, payload),
			);
		}
	} catch (error: unknown) {
		handleNodeUpdateError(error);
	}
};

export const getExtensionManifestMapper =
	({
		analyticsClient,
		editorActions,
		fieldsModules,
		renderConfigHelper,
		createRenderFunction,
		connectToForgeBlockedExtensions,
		updateFunction,
		autoConvertDefinitions,
		autoConvertMatcherToExtensionsMap,
		embeddedMacroContext,
		intl,
	}: ExtensionMapperParams) =>
	(extension: ForgeUIExtensionType): ForgeExtensionManifest => {
		const { sendEvent } = analyticsClient;
		const { appId, id, properties, environmentType, migrationKey, environmentKey } = extension;
		const title = createTitleWithEnvironmentInfo(
			properties.title || 'Forge UI Macro',
			environmentKey,
			environmentType,
		);

		const extensionIdWithoutPrefix = parse(id).resourceId!;
		const isCustomUI = getIsCustomUI(extension);
		const isUiKitTwo = extension.properties.render === 'native';

		const hasConfig = isUiKitTwo || isCustomUI ? !!properties.config : false;
		const hasCustomConfig = (isUiKitTwo || isCustomUI) && !!properties.config?.resource;
		const hasAutoConvertLinks = !!properties.autoConvert;
		const isInline = properties.layout === 'inline';
		const isBodiedExtension = properties.layout === 'bodied';
		// For UI Kit and custom UI apps with side context panel config only
		// Custom config openOnInsert is a different parameter in the manifest config object, and is rendered by renderConfigHelper()
		const openConfigOnInsert =
			(isUiKitTwo || isCustomUI) &&
			hasConfig &&
			!hasCustomConfig &&
			!!properties.config?.openOnInsert;

		const connectModuleKey = extractKeyFromId(extensionIdWithoutPrefix);

		const createExtensionProps = (macroId?: string): ForgeCreateExtensionProps => {
			const localId = macroId || createLocalId();
			const extensionId = id;
			const type = getExtensionType(extension);
			return {
				// Do not add any additional props in this object or in attrs without changing the adf schema
				type: type,
				attrs: {
					extensionKey: extensionIdWithoutPrefix,
					extensionType: EXTENSION_NAMESPACE,
					// Only parameters can change without changing the adf schema
					// Be careful as connect and editor reference node.attrs.parameters.X in multiple places
					parameters: {
						localId,
						extensionId,
						extensionTitle: title,
						layout: type,
						forgeEnvironment: environmentType,
						render: properties.render,
						embeddedMacroContext,
					},
					text: title,
					localId,
				},
				...(type === 'bodiedExtension'
					? {
							// include empty content so bodiedExtension doesn't throw error
							content: [{ type: 'paragraph', content: [] }],
						}
					: {}),
			};
		};

		const icon = <Icon label={properties?.title ?? ''} imgUrl={properties.icon} />;
		const macroIcons =
			properties.icon && properties.icon.length > 0
				? {
						'16': () => Promise.resolve(() => icon),
						'24': () => Promise.resolve(() => icon),
						'48': () => Promise.resolve(() => icon),
					}
				: manifestIcons;

		const getFieldsDefinition = createConnectToForgeGetFieldsDefinitionFunction({
			analyticsClient,
			connectToForgeBlockedExtensions,
			extension,
			hasCustomConfig,
		});

		const makeAutoConvertDropdownOptionsInManifest = () => {
			const node = editorActions?.getSelectedNode();
			const link = node?.attrs.parameters.autoConvertLink;
			return makeAutoConvertDropdownOptions(
				autoConvertDefinitions,
				autoConvertMatcherToExtensionsMap,
				editorActions,
				link,
			);
		};

		const getQuickInsertNode = async (actions: ExtensionAPI) => {
			let extensionProps = createExtensionProps();
			const { localId } = extensionProps.attrs.parameters;
			const { type: extensionType } = extensionProps;
			const isBodiedExtension = extensionType === 'bodiedExtension';

			if (hasCustomConfig) {
				// Skips merging node with payload that renders the config on insert
				if (extension.properties.config?.openOnInsert === false) {
					sendEvent(AnalyticsAction.FORGE_EXTENSION_INSERTED, {
						extension,
						localId,
					});
					return extensionProps;
				}

				const forgeExtensionNode = {
					...extensionProps.attrs,
					type: extensionType,
				};

				const payload = await new Promise<ForgeConfigPayload>((resolve) => {
					let isInitialInsertion = true;
					renderConfigHelper({
						extension,
						forgeExtensionNode,
						hasCustomConfig,
						isInitialInsertion: true, // We don't use the variable here because this is only executed once
						createRenderFunction,
						onSubmit: (payload: ForgeConfigPayload) => {
							if (isInitialInsertion) {
								isInitialInsertion = false;
								resolve(payload);
							} else if (actions) {
								// Update existing node
								updateNodeWithPayload(actions, localId, payload);
							}
						},
						onValidate: (data: unknown) =>
							validateForgeConfigPayload(data, {
								isBodiedExtension,
								isInitialInsertion,
								schema: editorActions?._privateGetEditorView?.()?.state.schema,
							}),
					});
				});

				extensionProps = {
					...extensionProps,
					...mergeNodeWithPayload(extensionProps.attrs, payload),
				};
			}

			sendEvent(AnalyticsAction.FORGE_EXTENSION_INSERTED, {
				extension,
				localId,
			});

			return extensionProps;
		};

		const isExtensionHidden = () => {
			return (
				isExperimentEnabled('platform_forge_macro_hidden_filtering') &&
				extension.type === 'xen:macro' &&
				'hidden' in properties &&
				properties.hidden === true
			);
		};

		const shouldBlockExtension = () => {
			return shouldBlockExtensionDueToAppAccessPolicy(extension) || isExtensionHidden();
		};
		const app = isExperimentEnabled('platform_editor_slash_app_category_analytics')
			? { key: appId ?? extensionIdWithoutPrefix.split('/')[0], source: 'ecosystem' as const }
			: undefined;

		return {
			title,
			description: properties.description,
			type: EXTENSION_NAMESPACE,
			key: extensionIdWithoutPrefix,
			icons: macroIcons,
			categories: properties.categories,
			connectModuleKey,
			migrationKey,
			migratedFrom: properties.migratedFrom,
			...(app ? { app } : {}),
			createExtensionProps,
			autoConvert: properties.autoConvert,
			modules: {
				...(shouldBlockExtension()
					? {}
					: {
							quickInsert: [
								{
									key: 'forge-macro',
									action: async (actions) => {
										const node = await getQuickInsertNode(actions);

										if (openConfigOnInsert && editorActions?._privateGetEditorView()) {
											if (isBodiedExtension) {
												renderBodiedExtensionIframe({
													extension,
													forgeExtensionNode: node.attrs,
													createRenderFunction,
												});
											}

											editorActions?.replaceSelection(node);

											// Workaround as the dispatch call within editorActions.replaceSelection() is async,
											// and we need to ensure that editInContextPanel is called after.
											setTimeout(
												() =>
													actions!.editInContextPanel(transformConfigBefore, async (params) =>
														transformConfigAfter(params),
													),
												0,
											);

											// We return undefined here to skip the additional replaceSelection() in editor-core
											// platform/packages/editor/editor-core/src/utils/extensions.tsx getItems()
											return;
										}

										return node;
									},
								},
							],
						}),
				nodes: {
					default: {
						type: 'extension',
						render: () => createRenderFunction(extension),
						update: hasConfig
							? (params, actions) => updateFunction(params, actions, extension, hasCustomConfig)
							: undefined,
						getFieldsDefinition,
					},
				},
				fields: fieldsModules,
				contextualToolbars: hasAutoConvertLinks
					? [
							{
								context: {
									nodeType: getExtensionType(extension),
									extensionKey: extensionIdWithoutPrefix,
									type: 'extension',
									extensionType: 'com.atlassian.ecosystem',
									shouldExclude: (node) => {
										return !node.attrs?.parameters.hasBeenAutoConverted;
									},
								},
								toolbarItems: [
									{
										key: `${extension.key}-convert-to-smartlink-toolbarItem`,
										label: intl.formatMessage(messages.convertToSmartlinkToolbarItemLabel),
										display: 'icon',
										icon: () =>
											import(
												/* webpackChunkName: "@atlaskit-internal_icon" */ '@atlaskit/icon/core/smart-link'
											).then((mod) => mod.default),
										action: async () => {
											/*We need the editorView object to determine the position of the node.
								By default, the safeInsert method inside replaceSelection inserts nodes in pos + 1 if the selected
								node is an extension. */

											const editorView = editorActions?._privateGetEditorView();
											const node = editorActions?.getSelectedNode();
											const originalLink: string = node?.attrs.parameters.autoConvertLink;
											if (editorView) {
												const { selection } = editorView.state;
												const insertPosition = selection.$from.pos;
												editorActions?.replaceSelection(
													isInline
														? {
																type: 'inlineCard',
																attrs: {
																	url: originalLink,
																},
															}
														: {
																type: 'paragraph',
																content: [
																	{
																		type: 'inlineCard',
																		attrs: {
																			url: originalLink,
																		},
																	},
																],
															},
													true,
													insertPosition,
												);

												sendEvent(AnalyticsAction.FORGE_MACRO_AUTO_CONVERT_TO_SMARTLINK, {
													extensionId: extension.key,
													forgeEnvironment: extension.environmentType,
													render: extension.properties.render,
													link: originalLink,
												});
											}
										},
										tooltip: 'Convert to link',
										ariaLabel: 'Convert to link',
									},
									{
										id: `${extension.key}-display-options-toolbarItem`,
										title: 'Display options',
										type: 'dropdown',
										options: () => makeAutoConvertDropdownOptionsInManifest(),
										tooltip: 'Display options',
									},
								],
							},
						]
					: [],
			},
		};
	};
